/* eslint-disable react/jsx-props-no-spreading */
import { useForm } from '@folklore/forms';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useEnterPincode } from '../../hooks/usePincode';

import { useContest } from '../../contexts/ContestContext';
import { useThemeClassName } from '../../contexts/ThemeContext';
import SubmitButton from '../buttons/SubmitButton';
import { Pincode } from '../fields';
import FieldLabel from '../fields/FieldLabel';

// import TextField from '../fields/Text';
// import * as AppPropTypes from '../../lib/PropTypes';
import styles from '../../../styles/forms/enter-pincode-form.module.scss';

interface EnterPincodeValue {
    code?: string;
    participation_id?: string;
}

interface EnterPincodeFormProps {
    value?: EnterPincodeValue;
    participation?: Participation;
    title?: React.ReactNode;
    withoutTitle?: boolean;
    submitLabel?: React.ReactNode;
    className?: string;
    onSuccess?: (user: Participant) => void;
    disabled?: boolean;
}

function EnterPincodeForm({
    value = null,
    participation = null,
    title = null,
    withoutTitle = false,
    submitLabel = null,
    className = null,
    onSuccess = null,
    disabled = false,
}: EnterPincodeFormProps) {
    const themeClassName = useThemeClassName(styles);
    const { access = null } = useContest();
    const { pincodeMask = '0000000' } = access || {};
    const route = useUrlGenerator();
    const action = route('pincode');

    const finalValue = useMemo(() => {
        return {
            participation_id: participation !== null ? participation.id : null,
            ...value,
        };
    }, [value, participation]);

    const onComplete = useCallback(
        ({ participant }) => {
            if (onSuccess !== null) {
                onSuccess(participant);
            }
        },
        [onSuccess],
    );

    const { enterPincodeAsync } = useEnterPincode();
    const postForm = useCallback(
        (postAction: string, postData: EnterPincodeValue) => enterPincodeAsync(postData),
        [enterPincodeAsync],
    );

    const {
        fields,
        onSubmit,
        errors,
        loading,
        generalError = null,
    } = useForm({
        fields: ['code'],
        action,
        postForm,
        onComplete,
        value: finalValue,
    });

    const { code: codeErrors = null } = errors || {};

    const hasErrors = (codeErrors !== null && codeErrors.length > 0) || generalError !== null;

    return (
        <form
            onSubmit={onSubmit}
            action={action}
            className={classNames([
                styles.container,
                themeClassName,
                className,
                { [styles.loading]: loading, [styles.disabled]: disabled },
            ])}
        >
            {!withoutTitle ? (
                <FieldLabel className={styles.label}>
                    {title || (
                        <FormattedMessage
                            defaultMessage="Enter your PIN code to start playing."
                            description="Form label"
                        />
                    )}
                </FieldLabel>
            ) : null}
            <div className={styles.formGroup}>
                <Pincode
                    className={styles.input}
                    {...fields.code}
                    placeholder={pincodeMask}
                    maxLength={10}
                    disabled={loading || disabled}
                />
                <SubmitButton
                    className={styles.submit}
                    type="submit"
                    disabled={loading || disabled}
                >
                    {submitLabel || (
                        <FormattedMessage defaultMessage="Submit" description="Button label" />
                    )}
                </SubmitButton>
            </div>

            {hasErrors ? (
                <div className={styles.errors}>
                    {(codeErrors || []).map((error, errorIndex) => (
                        <div key={`error-${errorIndex + 1}`}>{error}</div>
                    ))}
                    {generalError !== null ? <div>{generalError}</div> : null}
                </div>
            ) : null}
        </form>
    );
}

export default EnterPincodeForm;
